/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Card, Text } from '../../bphComponents/bundle';
import ContentFrame from '../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../components/ScrollAware';
import { AppHeaderScrollableText } from '../components/appNav/AppHeaderScrollable';

import { LanguageWithCountryCode } from '../localization/LocalizedLanguage';

import RouteNames from '../appAuthorized/RouteNames';
import ContentLoader from '../components/ContentLoader';
import ContentWrapper from '../components/ContentWrapper';

import { useNavigate } from '../hooks/useNavigate';
import { useActiveUser } from '../hooks/useUser';
import { useLists } from '../hooks/useLists';

const SetPreferredLanguage: React.FC = injectIntl((props) => {
  const { intl } = props;
  const [, navActions] = useNavigate();
  const [{ localeId }, userActions] = useActiveUser();
  const [{ localeList }] = useLists();

  const sortedLocaleList = localeList.sort((a, b) => {
    const aText = intl.formatMessage({ id: `${a.key}.name` });
    const bText = intl.formatMessage({ id: `${b.key}.name` });
    return aText.localeCompare(bText);
  });

  const [state, setState] = React.useState({
    isUpdating: false,
    isLanguageChanged: false,
    clickedId: null,
    locale: {
      id: localeId,
    },
  });

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          cancel={() => {
            navActions.goBackTo(RouteNames.accountSettings());
          }}
          submit={() => {
            const user = userActions.instantiateUser();
            user.setLocale(state.locale.id);

            setState({
              ...state,
              isUpdating: true,
            });

            userActions.addLocale(state.locale)
              .then(() => {
                userActions.apiUpdate(user.data)
                .then(() => {
                  navActions.goBackTo(RouteNames.accountSettings());
                }).catch(() => {
                  setState({
                    ...state,
                    isUpdating: false,
                  });
                });
              });
          }}
          color="white"
          submitDisabled={state.isUpdating || !state.isLanguageChanged}
        />

        <ContentLoader isContentLoaded={!state.isUpdating}>
          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableText
                id="settingsLanguage.title.text"
              />
              <ContentWrapper>
                <Card.CardShape>
                  <ul>
                    <Card.CardContentListItem hasBottomBorder>
                      <Text.TextCaption className="font-color-gsMedDark">
                        <FormattedMessage id="settings.language.field.label" />
                      </Text.TextCaption>
                    </Card.CardContentListItem>
                    {
                      sortedLocaleList.map(l => {
                        return (
                          <Card.SelectableCardContentListItem
                            key={l.id}
                            hasBottomBorder
                            isHighlighted={false}
                            isSelected={l.id === state.locale.id}
                            onClick={() => {
                              const { id } = l;
                              setState({
                                ...state,
                                isLanguageChanged: id !== localeId,
                                clickedId: id,
                                locale: l,
                              });
                            }}
                            selectedClassName="fill-color-profile-highlight"
                            highlightedClassName="profile-background-color-10"
                          >
                            <Text.TextBody>
                              <LanguageWithCountryCode locale={l} />
                            </Text.TextBody>
                          </Card.SelectableCardContentListItem>
                        );
                      })
                    }
                  </ul>
                </Card.CardShape>
              </ContentWrapper>
            </ScrollAwareScroller>
          </ContentFrame>
        </ContentLoader>
      </ScrollAwareContext>
    </div>
  );
});

export default SetPreferredLanguage;
